import React, { useState } from "react";
import { format } from "date-fns";

import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  useTheme
} from "@mui/material";
import { Circle } from "@mui/icons-material";

import { CalendarIcon, WalletIcon } from "../../../components/icons";
import { payments, PaymentProps } from "../../../data/pilot-payments";
import { tokens } from "../../../theme";

import { paginate } from '../../../utils/paginate';
import { PilotProps } from "../types/view-pilot";

interface PilotPaymentsProps {
  pilot: PilotProps | null;
}

export const PilotPayments: React.FC<PilotPaymentsProps> = ({ pilot }) => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(3);

  const paginated = paginate(payments, page, perPage);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);    
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  if (!pilot) return null;
  
  console.log({ pilot });

  return (
    <Box sx={{ p: 1, minHeight: "auto" }}>
      <Grid container spacing={2}>
        {/* Ratings Distribution */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ boxShadow: 0, height: "100%" }}>
            <CardContent>
              <Box
                display={'flex'}
                alignItems={'center'}
                sx={{ pb: '8px', borderBottom: 1, borderColor: 'divider', mb: '8px' }}
                gap={'4px'}>
                <WalletIcon color="disabled" sx={{ fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Wallet Balance
                </Typography>
              </Box>

              <Box flexDirection={'column'} display={'flex'} sx={{ my: 4 }} alignItems={'center'}>
                <Typography variant={'h4'} sx={{ fontWeight: 600 }}>$650</Typography>
                <Typography variant={'caption'}>Available for withdrawal</Typography>
              </Box>

              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'subtitle2'}>This Month</Typography>
                <Typography variant={'subtitle2'}>$120</Typography>
              </Box>
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'subtitle2'}>Pending</Typography>
                <Typography variant={'subtitle2'}>$650</Typography>
              </Box>

              <Box display={'flex'} mt={1} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'subtitle2'}>Total Earned (Month to Date)</Typography>
                <Typography variant={'subtitle2'}>$215</Typography>
              </Box>
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'subtitle2'}>Total Earned (Year to Date)</Typography>
                <Typography variant={'subtitle2'}>$1145</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Pilot Reviews */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ boxShadow: 0, height: "100%" }}>
            <CardContent>
              <Box
                display={'flex'}
                alignItems={'center'}
                sx={{ pb: '8px', borderBottom: 1, borderColor: 'divider', mb: '8px' }}
                gap={'4px'}>
                <CalendarIcon color="disabled" sx={{ fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Payment History
                </Typography>
              </Box>

              {payments && paginated?.map((pts: PaymentProps) => {
                return (
                  <Box
                    key={pts.id}
                    sx={{
                        border: '1px solid',
                        mb: 1,
                        borderRadius: 2,
                        padding: 1,
                        borderColor: backgroundColor
                    }}>
                    <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                      <Box>
                        <Typography variant={'subtitle2'} sx={{ fontWeight: 500, mb: -0.8 }}>{pts.event.name}</Typography>
                        <Typography variant={'caption'}>{pts.event.type}</Typography>
                      </Box>
                      <Typography variant={'h5'} sx={{ fontWeight: 600 }}>{`$${pts.amountDue}`}</Typography>
                    </Box>
                    <Box display={'flex'} alignItems={'center'} sx={{ mt: 1 }} justifyContent={'space-between'}>
                      <Box display={'flex'} alignItems={'center'} gap={.4}>
                        <Typography variant={'caption'}>{format(new Date(pts.date), "dd/MM/yyyy")}</Typography>
                        <Circle sx={{ fontSize: '5px' }} />
                        <Typography variant={'caption'} sx={{ textTransform: 'uppercase' }}>assignment</Typography>
                      </Box>
                      <Chip
                        size={'small'}
                        sx={{
                          bgcolor: pts.paid ? '#4BA66340' : '#F79E1B40',
                          borderRadius: 1,
                          color: pts.paid ? '#4BA663' : '#F79E1B'
                        }}
                        label={pts.paid ? 'Paid' : 'Not Paid'}
                      />
                    </Box>
                  </Box>
                );
              })}

              <Box sx={{ mt: 2 }}>
                <Box display={'flex'} alignItems={'center'} gap={.4}>
                  <Typography sx={{ cursor: 'pointer', fontWeight: 600 }} variant={'subtitle1'}>Page:</Typography>
                  {[...Array(Math.ceil(payments.length / perPage)).keys()].map(pg => (
                  <Box
                    key={pg}
                    onClick={() => setPage(pg + 1)}
                    sx={{
                      bgcolor: page === pg + 1 ? '#724E41' : '#FFF',
                      borderRadius: 1,
                      cursor: 'pointer',
                      px: 1.6,
                      py: .4,
                      fontSize: 14,
                      fontWeight: page === pg + 1 ? 500 : 600,
                      color: page === pg + 1 ? '#FFF' : '#000',
                      transition: 'all .4s ease'
                    }}>
                      <Typography sx={{ cursor: 'pointer' }} variant={'h6'}>{pg + 1}</Typography>
                  </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
